import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MdThumbDownOffAlt, MdThumbUpAlt, MdThumbDownAlt, MdThumbUpOffAlt } from "react-icons/md";
import { useMsal } from "@azure/msal-react";
import { ActionIcon, Button, Chip, Group, Input, Loader, LoadingOverlay, Modal, Stack, Textarea } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useHistoryManagerContext } from "../HistoryProviders/HistoryManagerContext";
import { FeedbackType } from "../types";
import { getToken, useLogin } from "../../authConfig";
import { useUserSettings } from "../../UserSettingsContext";

export interface FeedbackProps {
    conversationId?: string;
    turnIndex?: number;
}

export const Feedback = ({ conversationId, turnIndex = 0 }: FeedbackProps) => {
    const { t } = useTranslation();
    const [negativeFeedbackFormOpened, { open: openNegativeFeedbackForm, close: closeNegativeFeedbackForm }] = useDisclosure(false);

    const [like, setLike] = useState<boolean | null>(null);
    const [dislike, setDislike] = useState<boolean | null>(null);
    const [feedbackText, setFeedbackText] = useState<string>("");
    const [feedbackReason, setFeedbackReason] = useState<string | null>(null);
    const [isFeedbackInProgress, setIsFeedbackInProgress] = useState(false);

    const historyManager = useHistoryManagerContext();
    const client = useLogin ? useMsal().instance : undefined;
    const { userSettings, loading, error } = useUserSettings();

    if (!conversationId || loading || error || !userSettings) {
        return null;
    }

    const handleThumbUp = async () => {
        if (like) return;

        try {
            setIsFeedbackInProgress(true);
            setFeedbackText("");
            await submitFeedback(FeedbackType.Like);
            setLike(true);
            notifications.show({
                title: t("feedback.success"),
                message: t("feedback.successMessage"),
                color: "teal"
            });
        } catch (error) {
            console.error("Failed to submit feedback:", error);
            setLike(null);
            notifications.show({
                title: t("feedback.failed"),
                message: t("feedback.errorMessage"),
                color: "red"
            });
        } finally {
            setDislike(null);
            setIsFeedbackInProgress(false);
        }
    };

    const handleThumbDown = () => {
        if (dislike) return;
        openNegativeFeedbackForm();
    };

    const handleSubmitNegativeFeedbackForm = async () => {
        try {
            setIsFeedbackInProgress(true);
            await submitFeedback(FeedbackType.Dislike, feedbackText, feedbackReason ?? undefined);
            setDislike(true);
            notifications.show({
                title: t("feedback.success"),
                message: t("feedback.successMessage"),
                color: "teal"
            });
        } catch (error) {
            console.error("Failed to submit feedback:", error);
            setDislike(null);
            notifications.show({
                title: t("feedback.failed"),
                message: t("feedback.errorMessage"),
                color: "red"
            });
        } finally {
            setFeedbackText("");
            setFeedbackReason(null);
            setLike(null);
            setIsFeedbackInProgress(false);
            closeNegativeFeedbackForm();
        }
    };

    const submitFeedback = async (feedbackType: FeedbackType, comment: string = "", reason?: string) => {
        const token = client ? await getToken(client) : undefined;
        await historyManager.giveFeedback({
            conversationId: conversationId!,
            positive: feedbackType !== FeedbackType.Dislike,
            turnIndex,
            comment: comment || undefined,
            idToken: token,
            reason
        });
    };

    return (
        <>
            <Group gap="xs" justify="flex-end">
                {isFeedbackInProgress ? (
                    <Loader size="sm" />
                ) : (
                    <>
                        <ActionIcon variant="transparent" color="blue.9" onClick={handleThumbUp}>
                            {like ? <MdThumbUpAlt size={24} /> : <MdThumbUpOffAlt size={24} />}
                        </ActionIcon>
                        <ActionIcon variant="transparent" color="blue.9" onClick={handleThumbDown}>
                            {dislike ? <MdThumbDownAlt size={24} /> : <MdThumbDownOffAlt size={24} />}
                        </ActionIcon>
                    </>
                )}
            </Group>

            <Modal opened={negativeFeedbackFormOpened} onClose={closeNegativeFeedbackForm} title={t("feedback.title")} size="lg" centered>
                <LoadingOverlay visible={isFeedbackInProgress} zIndex={1000} loaderProps={{ type: "dots" }} />
                <Stack gap="sm">
                    <Stack gap={4}>
                        <Input.Label>{t("feedback.reasonLabel")}</Input.Label>
                        <Group gap="xs">
                            {[
                                { value: "incorrect_answer", label: t("feedback.reason.incorrectAnswer") },
                                { value: "incomplete_answer", label: t("feedback.reason.incompleteAnswer") },
                                { value: "unsuitable_asset", label: t("feedback.reason.unsuitableAsset") }
                            ].map(({ value, label }) => (
                                <Chip
                                    key={value}
                                    checked={feedbackReason === value}
                                    onChange={() => setFeedbackReason(feedbackReason === value ? null : value)}
                                >
                                    {label}
                                </Chip>
                            ))}
                        </Group>
                    </Stack>
                    <Textarea
                        label={t("feedback.details")}
                        description={t("feedback.description")}
                        autosize
                        minRows={2}
                        maxRows={4}
                        value={feedbackText}
                        onChange={ev => setFeedbackText(ev.currentTarget.value)}
                    />
                    <Group justify="flex-end" gap="sm">
                        <Button variant="outline" size="xs" onClick={closeNegativeFeedbackForm}>
                            {t("feedback.cancelButton")}
                        </Button>
                        <Button variant="filled" size="xs" onClick={handleSubmitNegativeFeedbackForm}>
                            {t("feedback.submitButton")}
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </>
    );
};
